import { FC, useCallback, useEffect, useMemo } from 'react'

import {
  FormInput,
  FormSwitch,
  resetAllModifiedDataByKey,
  useGetFormData,
} from 'cng-common/features/form'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'

import { useSaveEspSponsorMutation } from '../../api/espApi'
import { ESP_TRANSLATION_KEYS } from '../../configs/translations'
import { ESP_SPONSOR_FORM_KEY } from '../../constants'
import { IEspSponsorInput, IEspSponsorOption } from '../../model/types'

import { toEspSponsorInput } from './espSponsorInput'

import styles from './EspSponsorsCard.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.sponsors
const FORM_KEY = ESP_SPONSOR_FORM_KEY

interface IEspSponsorDialogContentProps {
  sponsor: IEspSponsorOption | null
  onClose: () => void
}

const EspSponsorDialogContent = ({ sponsor, onClose }: IEspSponsorDialogContentProps) => {
  const dispatch = useAppDispatch()
  const [saveSponsor, { isLoading }] = useSaveEspSponsorMutation()
  const initialForm = useMemo(() => toEspSponsorInput(sponsor), [sponsor])
  const [{ formData }] = useGetFormData<IEspSponsorInput>(FORM_KEY, initialForm)

  const handleSave = useCallback(async () => {
    const validation = FormValidationBuilder.build(FORM_KEY)
    const name = formData.Name.trim()

    if ((await validation.call('Name', formData.Name)) || !name) {
      return
    }

    const input: IEspSponsorInput = {
      Name: name,
      Title: formData.Title.trim(),
      ExecutiveLevel: formData.ExecutiveLevel.trim(),
      Location: formData.Location.trim(),
      IsActive: Boolean(formData.IsActive),
    }

    try {
      await saveSponsor({ entryId: sponsor?.EntryId, input }).unwrap()
      onClose()
    } catch {
      // the api layer already surfaced the error toast; keep the dialog open
    }
  }, [formData, onClose, saveSponsor, sponsor?.EntryId])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(FORM_KEY))
    }
  }, [dispatch])

  return (
    <div className={styles.dialog}>
      <FormInput
        isRequired
        formKey={FORM_KEY}
        field="Name"
        defaultValue={initialForm.Name}
        type="text"
        label={KEYS.fieldName}
      />
      <FormInput
        formKey={FORM_KEY}
        field="Title"
        defaultValue={initialForm.Title}
        type="text"
        label={KEYS.fieldTitle}
      />
      <div className={styles.two}>
        <FormInput
          formKey={FORM_KEY}
          field="ExecutiveLevel"
          defaultValue={initialForm.ExecutiveLevel}
          type="text"
          label={KEYS.fieldLevel}
        />
        <FormInput
          formKey={FORM_KEY}
          field="Location"
          defaultValue={initialForm.Location}
          type="text"
          label={KEYS.fieldRegion}
        />
      </div>
      <FormSwitch
        formKey={FORM_KEY}
        field="IsActive"
        defaultValue={initialForm.IsActive}
        label={KEYS.fieldActive}
      />
      <p className={styles.hint}>
        <Translation text={KEYS.activeHint} span />
      </p>

      <div className={styles.footer}>
        <Button variant="text" color="grey" onClick={onClose} disabled={isLoading}>
          <Translation text={KEYS.cancel} />
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          <Translation text={KEYS.save} />
        </Button>
      </div>
    </div>
  )
}

interface IEspSponsorDialogProps {
  /** The sponsor to edit; null adds a new one. */
  sponsor: IEspSponsorOption | null
  show: boolean
  onClose: () => void
}

export const EspSponsorDialog: FC<IEspSponsorDialogProps> = ({ sponsor, show, onClose }) => (
  <ModalDialog<IEspSponsorDialogContentProps>
    show={show}
    onClose={onClose}
    size="sm"
    header={() => (
      <div className={styles.dialogTitle}>
        <Translation text={sponsor ? KEYS.dialogEditTitle : KEYS.dialogAddTitle} span />
      </div>
    )}
    content={EspSponsorDialogContent}
    contentProps={{ sponsor, onClose }}
    closeOnOutsideClick={false}
  />
)

EspSponsorDialog.displayName = 'EspSponsorDialog'
